
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {TextSprite} from "../../TextSprite.js";
import {LEVEL, TIER, RULES, visibleAbove, edgeFits, GeometryGrid, solveLayout, resultClass} from "./labelLayout.js";
import {getLabelRevision, bumpLabelRevision, applyLabelVisibility, setLabelLevel, resetLabelManagement, labelHome, setLabelAnchorOverride, clearLabelAnchorOverride, SPRITE_PX, PX_PER_LOCAL} from "./labelState.js";
import * as hoverApi from "./hoverState.js";
import {getHoverTarget, hoverTick, setHoverChangeListener} from "./hoverState.js";

// Fælles visningsregler for labels (prioritet, plads, hover) - se LABEL-STRATEGI.md afsnit 15 og LABEL-IMPLEMENTERING-STATUS.md.
//
// Styrede kilder (ejerne beholder faglige indhold og geometrisk anker; her afgøres kun prioritet, trin, synlighed og placering):
//   - FeatureVisual: resultlabel, delmål, punktlabels (alle registreringstyper), hjælpelabels via userData.auxiliaryVisual.auxLabels()
//   - PipeVisual (isPipeVisual): rørets label (= værktøjets resultat)
// Labels uden for manageren (native Measure/Volume, GCP, værktøjernes forhåndsvisninger, DOM-labels som POI-nåle og graverapport) får ingen
// nye visningsregler, men deres synlige rektangler indgår som forhindringer (_collectExternal, _pollObstacles).
//
// Skel: pvWanted = brugerens valg (ejes af ejeren), pvPeek = midlertidig visning (hover/redigering), pvAuto = skjult automatisk (her).
// Layoutet køres kun ved ændringer (kamera, vindue, label-/valg-/hover-tilstand, paneler), højst ~20 Hz under navigation.

const MIN_LAYOUT_INTERVAL_MS = 50;
const SAFETY_PASS_MS = 500;
const SAFETY_LAYOUT_MS = 1000;
const OBSTACLE_POLL_MS = 150;
const EXTERNAL_SCAN_MS = 250;
const MAX_LEADERS = 64;

// Tegnerækkefølge pr. tier (hvem ligger øverst, når labels alligevel overlapper) - uafhængigt af, hvem der får pladsen
const RENDER_ORDER = [17, 16, 15, 14, 13, 12, 11];

export class LabelManager {
	constructor(module){
		this.module = module;
		this.rules = RULES;
		this.enabled = true;
		this.hover = hoverApi; // hovermålet (hoverState.js): læses/styres af modulet; udstillet til afprøvning

		this.cam = new Float64Array(16);
		this.proj = new Float64Array(16);
		this.W = 0;
		this.H = 0;
		this.revision = -1;
		this.selectionKey = null;
		this.selectionLength = -1;
		this.obstacleRevision = 0;
		this._obstacleHash = "";
		this._obstacleAt = 0;
		this.obstacleProvider = null;

		this.pending = false;
		this.lastLayoutAt = 0;
		this.lastPassAt = 0;
		this.lastSafetyAt = 0;
		this.hasManaged = false;

		this.pv = new THREE.Matrix4();
		this.ortho = false;
		this.ppmK = 0;
		this.near = 0.1; this.far = 1e6;
		this.camX = 0; this.camY = 0; this.camZ = 0;
		this.right = new THREE.Vector3(1, 0, 0);
		this.up = new THREE.Vector3(0, 1, 0);
		this.forward = new THREE.Vector3(0, 0, -1);

		this.recs = [];
		this.active = [];
		this.previous = [];
		this.stamp = 0;
		this.obstacles = [];
		this.external = [];
		this.grid = new GeometryGrid();
		this.env = {W: 0, H: 0, rules: RULES, obstacles: this.obstacles, grid: this.grid, placed: [], quiet: null};
		this.extLabels = []; // labels uden for manageren (TextSprite), fundet ved periodisk scanning
		this._extScanAt = -1e9;
		this._extSignature = 0;
		this._anchorOut = {x: 0, y: 0, z: 0};
		this.registered = new Set();
		this.multiSet = new Set();
		this._solvable = [];
		this._segScratch = [];
		this._tmp = {x: 0, y: 0, f: 1, ppm: 0};
		this._autoLabels = new Set();
		this._autoResults = new Set();

		// punkter i skærmrum for det objekt, der behandles
		this.px = new Float64Array(256);
		this.py = new Float64Array(256);
		this.pok = new Uint8Array(256);
		this.pf = new Float64Array(256);
		this.ppm = new Float64Array(256);

		this.leaders = [];
		this.leaderObject = null;

		// hover: ændringer af målet udløser nyt layout
		setHoverChangeListener(bumpLabelRevision);

		this.stats = {layouts: 0, passes: 0, lastLayoutMs: 0, maxLayoutMs: 0, totalLayoutMs: 0, collectMs: 0, solveMs: 0, applyMs: 0, recs: 0, shown: 0, forced: 0};
	}

	// Frontend (eller anden vært) kan oplyse, hvilke flader der dækker 3D-vinduet: fn() => [{left, top, right, bottom}] i vinduets
	// (client-)koordinater. Kaldes højst hver 250. ms og kun når der er styrede labels.
	setObstacleProvider(fn){
		this.obstacleProvider = typeof fn === "function" ? fn : null;
		this.obstacleRevision++;
	}

	// Slår den fælles styring fra/til (fx til sammenligning): fra = labels opfører sig som før (ingen automatisk skjulning/placering)
	setEnabled(enabled){
		enabled = !!enabled;
		if(enabled === this.enabled) return;
		this.enabled = enabled;
		if(!enabled){
			for(const rec of this.active) resetLabelManagement(rec.label);
			this.active.length = 0;
			this.previous.length = 0;
			this.leaders.length = 0;
			this._updateLeaders();
			this._publishAutoState(this._autoLabels.clear() || this._autoLabels, this._autoResults.clear() || this._autoResults);
		}
		this.pending = true;
		this.revision = -1;
		this._requestRedraw();
	}

	// ---------------------------------------------------------------------------------------------------------------------
	// Per frame. Returnerer true, når labelskala/forskydning skal opdateres i denne frame (kamera eller labels er ændret).

	beginFrame(camera, W, H, now){
		if(!this.enabled){
			this._updateAxes(camera);
			return true;
		}
		hoverTick(now); // et forladt hovermål udløber efter linger

		let changed = W !== this.W || H !== this.H;
		const e1 = camera.matrixWorld.elements, e2 = camera.projectionMatrix.elements;
		for(let i = 0; i < 16; i++){
			if(e1[i] !== this.cam[i]){ this.cam[i] = e1[i]; changed = true; }
			if(e2[i] !== this.proj[i]){ this.proj[i] = e2[i]; changed = true; }
		}
		this.W = W; this.H = H;

		const multi = this.module.multiSelection;
		const selectionLength = multi ? multi.entries.length : 0;
		let structural = false;
		const revision = getLabelRevision();
		if(revision !== this.revision || multi !== this.selectionKey || selectionLength !== this.selectionLength){
			structural = true;
			this.revision = revision;
			this.selectionKey = multi;
			this.selectionLength = selectionLength;
		}

		if(this.hasManaged && now - this._obstacleAt >= OBSTACLE_POLL_MS){
			this._obstacleAt = now;
			if(this._pollObstacles()) structural = true;
		}
		if(this.hasManaged){
			if(now - this._extScanAt >= EXTERNAL_SCAN_MS){
				this._extScanAt = now;
				this._scanExternalLabels();
			}
			// labels uden for manageren kan flytte sig uden at melde det (native målinger, værktøjernes forhåndsvisning): ændret placering,
			// synlighed eller tekst giver nyt layout
			const signature = this._externalSignature();
			if(signature !== this._extSignature){ this._extSignature = signature; structural = true; }
		}

		if(changed) this.pending = true;
		if(structural) this.pending = true;

		let layoutDue = false;
		if(this.pending && (structural || now - this.lastLayoutAt >= MIN_LAYOUT_INTERVAL_MS)) layoutDue = true;
		else if(now - this.lastSafetyAt >= SAFETY_LAYOUT_MS && this.hasManaged){
			layoutDue = true;
		}

		if(layoutDue){
			this.lastSafetyAt = now;
			this._layout(camera, W, H, now);
			this.pending = false;
			this.lastLayoutAt = now;
			this.lastPassAt = now;
			this.stats.passes++;
			this._updateAxes(camera);
			return true;
		}

		if(changed || structural || now - this.lastPassAt >= SAFETY_PASS_MS){
			this.lastPassAt = now;
			this.stats.passes++;
			this._updateAxes(camera);
			return true;
		}

		return false;
	}

	_updateAxes(camera){
		this.right.setFromMatrixColumn(camera.matrixWorld, 0);
		this.up.setFromMatrixColumn(camera.matrixWorld, 1);
		this.forward.setFromMatrixColumn(camera.matrixWorld, 2).negate();
	}

	// Efter at alle visuals har fået skala/forskydning: forbindelseslinjerne (følger labelens aktuelle skærmforskydning).
	endFrame(){
		this._updateLeaders();
	}

	// ---------------------------------------------------------------------------------------------------------------------
	// Forhindringer (paneler m.m.)

	_pollObstacles(){
		let rects = [];
		if(this.obstacleProvider){
			try{
				rects = this.obstacleProvider() || [];
			}catch(error){
				rects = [];
			}
		}
		const canvas = this.module.viewer.renderer.domElement;
		const box = canvas.getBoundingClientRect();
		// DOM-labels oven på 3D-vinduet (POI-nåle, graverapportens labels) markerer sig med data-pv-obstacle
		const domMarked = this.module.viewer.renderArea ? this.module.viewer.renderArea.querySelectorAll("[data-pv-obstacle]") : [];
		if(domMarked.length > 0){
			rects = rects.slice();
			for(const el of domMarked){
				if(el.style.display === "none") continue;
				const r = el.getBoundingClientRect();
				if(r.width > 0 && r.height > 0) rects.push({left: r.left, top: r.top, right: r.right, bottom: r.bottom});
			}
		}
		let hash = box.left + "," + box.top;
		for(const r of rects){
			hash += "|" + Math.round(r.left) + "," + Math.round(r.top) + "," + Math.round(r.right) + "," + Math.round(r.bottom);
		}
		if(hash === this._obstacleHash) return false;
		this._obstacleHash = hash;
		this.external.length = 0;
		for(const r of rects){
			const x0 = r.left - box.left, y0 = r.top - box.top, x1 = r.right - box.left, y1 = r.bottom - box.top;
			if(x1 <= 0 || y1 <= 0 || x0 >= this.W || y0 >= this.H) continue;
			this.external.push({x0, y0, x1, y1});
		}
		this.obstacleRevision++;
		return true;
	}

	// ---------------------------------------------------------------------------------------------------------------------
	// Layout

	// Projicerer til skærm-px (out.x/out.y). out.f = afstand/dybde: labels skaleres efter afstanden til kameraet (ScreenSpaceScale), så de fylder
	// f gange så mange px, som teksten alene tilsiger, ude i siden af et perspektivbillede (f = 1 i midten og ved ortografisk kamera).
	// out.ppm = px pr. meter ved punktet.
	_project(x, y, z, out){
		const e = this.pv.elements;
		const w = e[3] * x + e[7] * y + e[11] * z + e[15];
		if(!(w > 1e-9)) return false;
		if(this.ortho){
			out.f = 1;
			out.ppm = this.ppmK;
		}else{
			const dx = x - this.camX, dy = y - this.camY, dz = z - this.camZ;
			out.f = Math.sqrt(dx * dx + dy * dy + dz * dz) / w;
			out.ppm = this.ppmK / w;
		}
		const zc = (e[2] * x + e[6] * y + e[10] * z + e[14]) / w;
		if(zc < -1 || zc > 1) return false;
		out.x = ((e[0] * x + e[4] * y + e[8] * z + e[12]) / w + 1) * 0.5 * this.W;
		out.y = (1 - (e[1] * x + e[5] * y + e[9] * z + e[13]) / w) * 0.5 * this.H;
		return true;
	}

	_layout(camera, W, H, now){
		const t0 = performance.now();
		this.pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
		this.ortho = !!camera.isOrthographicCamera;
		this.ppmK = camera.projectionMatrix.elements[5] * H / 2;
		this.near = camera.near; this.far = camera.far;
		this.camX = camera.matrixWorld.elements[12]; this.camY = camera.matrixWorld.elements[13]; this.camZ = camera.matrixWorld.elements[14];
		this.stamp++;

		const env = this.env;
		env.W = W; env.H = H; env.quiet = null; env.now = now;
		this.recs.length = 0;
		this.grid.clear();
		this.obstacles.length = 0;
		for(const o of this.external) this.obstacles.push(o);
		this._collectExternal();

		this.registered.clear();
		for(const v of this.module.visuals.values()) this.registered.add(v);
		this.multiSet.clear();
		if(this.module.multiSelection){
			for(const entry of this.module.multiSelection.entries) this.multiSet.add(entry.visual);
		}

		// aktiv handling (oprettelse/træk) har forrang: hover på andre objekter undertrykkes
		let workingVisual = null;
		let primaryAnchor = null;
		const children = this.module.scene.children;
		for(let i = 0; i < children.length; i++){
			const child = children[i];
			if(!child.isFeatureVisual || !child.visible) continue;
			if(child.isDragging || !this.registered.has(child)){ workingVisual = child; break; }
		}
		const target = getHoverTarget();

		let managedCount = 0;
		let order = 0;
		for(let i = 0; i < children.length; i++){
			const child = children[i];
			if(!child.visible) continue;
			if(child.isFeatureVisual){
				managedCount++;
				order = this._collectFeature(child, order, workingVisual, target);
			}else if(child.isPipeVisual){
				managedCount++;
				order = this._collectPipe(child, order, workingVisual, target);
			}
		}
		this.hasManaged = managedCount > 0;

		// regler har allerede skjult nogle (plet, ikke tændt, bag kameraet); resten får plads efter tier, prioritet og overlap
		const solvable = this._solvable;
		solvable.length = 0;
		for(const rec of this.recs){
			if(rec.maxLevel > LEVEL.HIDDEN) solvable.push(rec);
		}
		const t1 = performance.now();
		solveLayout(solvable, env);
		const t2 = performance.now();

		const changed = this._apply();
		if(changed) this._requestRedraw();

		const t3 = performance.now();
		const ms = t3 - t0;
		const s = this.stats;
		s.collectMs += t1 - t0; s.solveMs += t2 - t1; s.applyMs += t3 - t2;
		s.layouts++;
		s.lastLayoutMs = ms;
		s.totalLayoutMs += ms;
		if(ms > s.maxLayoutMs) s.maxLayoutMs = ms;
		s.recs = this.recs.length;
		return primaryAnchor;
	}

	_requestRedraw(){
		const viewer = this.module.viewer;
		if(typeof viewer._requestRedraw === "function") viewer._requestRedraw(); else viewer.needsRedraw = true;
	}

	_ensurePointArrays(n){
		if(this.px.length >= n) return;
		const size = Math.max(n, this.px.length * 2);
		this.px = new Float64Array(size);
		this.py = new Float64Array(size);
		this.pok = new Uint8Array(size);
		this.pf = new Float64Array(size);
		this.ppm = new Float64Array(size);
	}

	_rec(label, kind, owner, index){
		const u = label.userData;
		let rec = u.pvRec;
		if(!rec){
			rec = u.pvRec = {
				id: label.id, order: 0, owner: owner.id, kind, tier: TIER.SECONDARY, forced: false, maxLevel: LEVEL.HIDDEN,
				sizes: [null, null, null, null], sizeObj: [null, {w: 0, h: 0}, {w: 0, h: 0}, {w: 0, h: 0}],
				ax: 0, ay: 0, edge: {x0: 0, y0: 0, x1: 0, y1: 0}, centerDist: 0, priority: 0,
				visible: false, level: LEVEL.HIDDEN, slot: null, leader: false, cx: 0, cy: 0, reason: "",
				zoomVis: false, fits: false, label, owner_: owner, index, peek: false, stamp: 0,
				f: 1, bw: [0, 0, 0, 0], bh: [0, 0, 0, 0], s1: null, s2: null, s3: null, anchorOnly: false, boost: 0,
			};
		}
		rec.softOwn = kind === "result" || kind === "pipe" || kind === "aux";
		rec.boost = 0; rec.silent = false;
		rec.kind = kind; rec.owner = owner.id; rec.owner_ = owner; rec.index = index;
		rec.onScreen = true; rec.maxLevel = LEVEL.HIDDEN; rec.forced = false; rec.anchorOnly = false; rec.priority = 0; rec.reason = "";
		return rec;
	}

	// Måler trappens trin (kun ved ny tekst) og skalerer med den aktuelle perspektivfaktor (rec.f). Trin uden tekst tilbydes ikke.
	_setSizes(rec, label){
		const u = label.userData;
		// Målingen følger altid den aktuelle tekst: ejerens trappe, eller (labels uden trappe) labelens egen tekst. Teksten og dens størrelse
		// stammer dermed fra samme data - også efter redigering.
		const ladder = u.pvLadder;
		const l1 = ladder ? ladder[1] : label.text, l2 = ladder ? ladder[2] : null, l3 = ladder ? ladder[3] : label.text;
		const pad0 = u.pvIconPad || 0;
		if(rec.s1 !== l1 || rec.s2 !== l2 || rec.s3 !== l3 || rec.sp !== pad0){
			rec.s1 = l1; rec.s2 = l2; rec.s3 = l3; rec.sp = pad0;
			for(let level = LEVEL.COMPACT; level <= LEVEL.FULL; level++){
				const text = level === LEVEL.COMPACT ? l1 : (level === LEVEL.MEDIUM ? l2 : l3);
				if(text == null){ rec.bw[level] = 0; rec.bh[level] = 0; continue; }
				const pad = (u.pvIconPad && text.indexOf("\n") < 0) ? u.pvIconPad : 0;
				const m = TextSprite.measure(text, label.fontsize, 4, "Arial", pad);
				rec.bw[level] = m.width * SPRITE_PX; rec.bh[level] = m.height * SPRITE_PX;
			}
		}
		const f = rec.f;
		for(let level = LEVEL.COMPACT; level <= LEVEL.FULL; level++){
			if(rec.bw[level] === 0){ rec.sizes[level] = null; continue; }
			const o = rec.sizeObj[level];
			o.w = rec.bw[level] * f; o.h = rec.bh[level] * f;
			rec.sizes[level] = o;
		}
	}

	_collectFeature(visual, order, workingVisual, target){
		const n = visual.points.length;
		if(n < 1) return order;
		this._ensurePointArrays(n);
		const W = this.W, H = this.H;
		const tmp = this._tmp;

		// projicér objektets punkter én gang
		let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, okCount = 0;
		for(let i = 0; i < n; i++){
			const p = visual.points[i].position;
			if(this._project(p.x, p.y, p.z, tmp)){
				this.px[i] = tmp.x; this.py[i] = tmp.y; this.pf[i] = tmp.f; this.ppm[i] = tmp.ppm; this.pok[i] = 1;
				if(tmp.x < minX) minX = tmp.x; if(tmp.x > maxX) maxX = tmp.x;
				if(tmp.y < minY) minY = tmp.y; if(tmp.y > maxY) maxY = tmp.y;
				okCount++;
			}else{
				this.pok[i] = 0;
			}
		}
		const pad = 200;
		// et objekt, hvor nogle punkter ikke kan projiceres (bag kameraet/uden for dybdeintervallet), kan stadig krydse billedet: kun et objekt, hvor
		// alle punkter er projicerbare og ligger uden for billedet - eller intet er projicerbart - er reelt uden for visningen
		const partial = okCount < n;
		if(okCount === 0 || (!partial && (maxX < -pad || maxY < -pad || minX > W + pad || minY > H + pad))){
			// helt uden for billedet (eller bag kameraet): labelsene beholder deres seneste placering til de kommer tilbage
			this._keepOffscreen(visual);
			return order + 1;
		}
		let objectSize = Math.hypot(maxX - minX, maxY - minY);
		if(partial) objectSize = Math.max(objectSize, Math.hypot(W, H)); // strækker sig ud over det projicerbare: i hvert fald et stort objekt

		// geometri, labels skal holdes fri af
		const owner = visual.id;
		const segmentCount = visual.closed ? n : n - 1;
		for(let i = 0; i < segmentCount; i++){
			const j = (i + 1) % n;
			if(this.pok[i] && this.pok[j]) this.grid.addSegment(owner, this.px[i], this.py[i], this.px[j], this.py[j]);
		}
		for(let i = 0; i < n; i++){
			if(this.pok[i]) this.grid.addCircle(owner, this.px[i], this.py[i], this.rules.handleRadius);
		}

		// tilstandskontekst
		const registered = this.registered.has(visual);
		const creating = !registered;
		const dragging = !!visual.isDragging;
		const working = creating || dragging;
		const mayHover = !workingVisual || workingVisual === visual;
		const hov = !!target && target.visual === visual && mayHover;
		let hoverIndex = -1, hoverSeg = -1, hoverObject = false;
		if(hov){
			if(target.kind === "vertex") hoverIndex = target.index;
			else if(target.kind === "segment" || target.kind === "handle") hoverSeg = target.index;
			else hoverObject = true;
		}
		// under træk er det trukne punkt det, der redigeres
		const editIndex = dragging ? (hoverIndex >= 0 ? hoverIndex : -1) : -1;
		const hovered = hov;
		const selectedPrimary = !!visual.selected;
		const selectedOther = !selectedPrimary && this.multiSet.has(visual);
		const cx = W / 2, cy = H / 2;

		if(editIndex >= 0 && this.pok[editIndex]){
			const q = this.rules.quietRadius;
			this.env.quiet = {x0: this.px[editIndex] - q, y0: this.py[editIndex] - q, x1: this.px[editIndex] + q, y1: this.py[editIndex] + q};
		}

		// resultatlabel: værktøjets hovedresultat, vist HELT som værktøjet definerer det (ingen generel reduktion). Der er ingen størrelsesregel ud
		// over at objektet ikke må være skrumpet til et punkt; ellers afgør pladsen. Ligger ankeret uden for billedet, forankres resultatet til
		// den synlige del af objektets geometri (_anchorResult).
		const result = visual.resultLabel;
		if(result){
			const u = result.userData;
			const wanted = !!u.pvWanted;
			const peek = !wanted && (hovered || working);
			const rec = this._rec(result, "result", visual, 0);
			rec.order = order++;
			rec.peek = peek;
			if(wanted || peek || hovered){
				if(this._anchorResult(visual, result, n, tmp)){
					rec.ax = tmp.x; rec.ay = tmp.y; rec.f = tmp.f;
					rec.centerDist = Math.hypot(rec.ax - cx, rec.ay - cy);
					rec.priority = objectSize; // sekundært kriterium inden for samme prioritet
					rec.zoomVis = visibleAbove(rec.zoomVis, objectSize, this.rules.collapsedPx, this.rules.hysteresis);
					const cls = resultClass({working, selectedPrimary, selectedOther, hovered});
					rec.tier = cls.tier; rec.forced = cls.forced;
					rec.maxLevel = cls.tier === TIER.ORDINARY && !rec.zoomVis ? LEVEL.HIDDEN : LEVEL.FULL;
					this._setSizes(rec, result);
				}else{
					// intet synligt stykke af objektet at forankre til: skjult, men ikke "skjult automatisk" - objektet er reelt uden for visningen
					rec.maxLevel = LEVEL.HIDDEN;
					rec.silent = true;
				}
				this.recs.push(rec);
			}else{
				// ikke ønsket og ingen midlertidig visning
				rec.maxLevel = LEVEL.HIDDEN;
				this.recs.push(rec);
			}
		}

		// delmåls-labels (en pr. kant): vises, hvor kanten rummer dem; efter resultaterne i prioritet
		for(let i = 0; i < visual.segmentLabels.length; i++){
			const label = visual.segmentLabels[i];
			const j = (i + 1) % n;
			const u = label.userData;
			const wanted = !!u.pvWanted;
			// arbejdsoplysning: den netop lagte kant under oprettelse og kanterne ved det redigerede punkt; hover: den hoverede kant
			let active = false;
			if(creating && i === visual.segmentLabels.length - 1) active = true;
			else if(dragging && editIndex >= 0 && (i === editIndex || j === editIndex)) active = true;
			const hoverThis = hoverSeg === i;
			const peek = !wanted && (active || hoverThis);
			const rec = this._rec(label, "segment", visual, i);
			rec.order = order++;
			rec.peek = peek;
			if(!(wanted || peek || active || hoverThis) || !this.pok[i] || !this.pok[j]){
				rec.maxLevel = LEVEL.HIDDEN;
				this.recs.push(rec);
				continue;
			}
			const x0 = this.px[i], y0 = this.py[i], x1 = this.px[j], y1 = this.py[j];
			rec.edge.x0 = x0; rec.edge.y0 = y0; rec.edge.x1 = x1; rec.edge.y1 = y1;
			rec.ax = (x0 + x1) / 2; rec.ay = (y0 + y1) / 2; rec.f = (this.pf[i] + this.pf[j]) / 2;
			rec.centerDist = Math.hypot(rec.ax - cx, rec.ay - cy);
			this._setSizes(rec, label);
			const width = rec.sizes[LEVEL.FULL] ? rec.sizes[LEVEL.FULL].w : 0;
			const edgePx = Math.hypot(x1 - x0, y1 - y0);
			rec.priority = edgePx;
			if(active){
				rec.tier = TIER.ACTIVE; rec.forced = true; rec.maxLevel = LEVEL.FULL; rec.fits = true;
			}else if(hoverThis){
				rec.tier = TIER.HOVER; rec.maxLevel = LEVEL.FULL; rec.fits = true;
			}else{
				// pladsen langs kanten afgør, om delmålet kan stå der - ikke objektets samlede størrelse
				rec.fits = edgeFits(rec.fits, edgePx, width, this.rules);
				rec.tier = TIER.SECONDARY;
				rec.boost = selectedPrimary ? 1 : 0;
				rec.maxLevel = rec.fits ? LEVEL.FULL : LEVEL.HIDDEN;
			}
			this.recs.push(rec);
		}

		// punktkoordinater: det rigeste trin, der kan stå frit (kote -> X/Y/Z -> rolle + X/Y/Z); skjules kun, når punktet ikke længere kan
		// aflæses (px pr. meter) eller der ikke er plads. De viger for resultater og delmål.
		for(let i = 0; i < visual.vertexLabels.length; i++){
			const label = visual.vertexLabels[i];
			const u = label.userData;
			const wanted = !!u.pvWanted;
			const isFocus = i === hoverIndex || i === editIndex;
			const peek = !wanted && isFocus;
			if(!(wanted || peek) || !this.pok[i]) continue;
			const rec = this._rec(label, "vertex", visual, i);
			rec.order = order++;
			rec.peek = peek;
			rec.ax = this.px[i]; rec.ay = this.py[i]; rec.f = this.pf[i];
			rec.centerDist = Math.hypot(rec.ax - cx, rec.ay - cy);
			rec.priority = -rec.centerDist;
			if(isFocus){
				rec.tier = i === editIndex ? TIER.ACTIVE : TIER.HOVER;
				rec.forced = i === editIndex;
				rec.maxLevel = LEVEL.FULL;
			}else{
				rec.zoomVis = visibleAbove(rec.zoomVis, this.ppm[i], this.rules.pointMinPpm, this.rules.hysteresis);
				// punktets navn/rolle (fuldt trin med overskrift) er objektets identifikation; ren koordinat/kote er det mindst vigtige
				rec.tier = u.pvLadder && u.pvLadder[LEVEL.FULL] != null ? TIER.IDENT : TIER.TERTIARY;
				rec.boost = selectedPrimary ? 1 : 0;
				rec.maxLevel = rec.zoomVis ? LEVEL.FULL : LEVEL.HIDDEN;
			}
			this._setSizes(rec, label);
			this.recs.push(rec);
		}

		// hjælpelabels (hjælpedelmål, stationer): en del af målingens aflæsning, men sekundære; aldrig under redigering
		const aux = visual.userData.auxiliaryVisual;
		if(aux && typeof aux.auxLabels === "function"){
			const labels = aux.auxLabels();
			for(let k = 0; k < labels.length; k++){
				const label = labels[k];
				const wanted = !!label.userData.pvWanted;
				const rec = this._rec(label, "aux", visual, k);
				rec.order = order++;
				rec.anchorOnly = true;
				const p = label.position;
				if(!wanted || !this._project(p.x, p.y, p.z, tmp)){
					rec.maxLevel = LEVEL.HIDDEN;
					rec.onScreen = !wanted; // uden for billedet: urørt
					this.recs.push(rec);
					continue;
				}
				rec.ax = tmp.x; rec.ay = tmp.y; rec.f = tmp.f;
				rec.centerDist = Math.hypot(rec.ax - cx, rec.ay - cy);
				rec.priority = objectSize;
				this._setSizes(rec, label);
				// hjælpeværdier hører til målingen og vises også under oprettelse og redigering, men viger for resultater
				rec.tier = TIER.SECONDARY;
				rec.boost = selectedPrimary ? 1 : 0;
				rec.maxLevel = LEVEL.FULL;
				this.recs.push(rec);
			}
		}

		return order;
	}

	// Rørmålingens label ER værktøjets resultat (dimension, godstykkelse og koter): vises helt, som værktøjet definerer det.
	_collectPipe(pipe, order, workingVisual, target){
		const label = pipe.label;
		const result = pipe.result;
		const tmp = this._tmp;
		const u = label.userData;
		const wanted = !!u.pvWanted;
		const hov = !!target && target.visual === pipe && !workingVisual;
		const selectedPrimary = !!pipe.selected;
		const selectedOther = !selectedPrimary && this.multiSet.has(pipe);
		// rørets label ER værktøjets resultat og klassificeres som alle andre resultater (rolle "result", resultClass); et rør under oprettelse
		// (preview, ikke registreret) er nødvendig arbejdsinformation
		const creating = !this.registered.has(pipe);
		const rec = this._rec(label, "result", pipe, 0);
		rec.order = order++;
		rec.peek = !wanted && (hov || creating);
		if(!result || !(wanted || rec.peek || hov || selectedPrimary || creating) || !this._project(result.station.x, result.station.y, result.station.z, tmp)){
			rec.maxLevel = LEVEL.HIDDEN;
			rec.onScreen = !(wanted || rec.peek) || !result;
			this.recs.push(rec);
			return order;
		}
		const cx = this.W / 2, cy = this.H / 2;
		rec.ax = tmp.x; rec.ay = tmp.y; rec.f = tmp.f;
		rec.centerDist = Math.hypot(rec.ax - cx, rec.ay - cy);
		// rørets skærmstørrelse: længste af sektionslængden og diameteren (som objektstørrelsen for øvrige målinger)
		const sizePx = Math.max(result.sectionLength, result.radius * 2) * tmp.ppm;
		rec.zoomVis = visibleAbove(rec.zoomVis, sizePx, this.rules.collapsedPx, this.rules.hysteresis);
		rec.priority = sizePx;
		const cls = resultClass({working: creating, selectedPrimary, selectedOther, hovered: hov});
		rec.tier = cls.tier; rec.forced = cls.forced;
		rec.maxLevel = cls.tier === TIER.ORDINARY && !rec.zoomVis ? LEVEL.HIDDEN : LEVEL.FULL;
		this._setSizes(rec, label);
		this.recs.push(rec);
		return order;
	}

	// Finder resultatlabelens skærmanker. Normalt ejerens eget anker (labelHome). Ligger det uden for arbejdsfladen (eller kan ikke projiceres), og
	// en tilstrækkelig del af objektets geometri er synlig, flyttes ankeret til midten af det længste synlige stykke af geometrien, så et
	// væsentligt synligt objekt ikke mister sit resultat. Er intet synligt, returneres false. Ankeret flyttes kun i selve label.position;
	// ejerens anker huskes (pvHome) og genindsættes, når det igen er synligt.
	_anchorResult(visual, label, n, out){
		const home = labelHome(label);
		const u = label.userData;
		const rules = this.rules;
		const inset = u.pvOverride ? rules.anchorReturnInset : rules.anchorInset;
		if(this._project(home.x, home.y, home.z, out) && out.x >= inset && out.x <= this.W - inset && out.y >= inset && out.y <= this.H - inset){
			clearLabelAnchorOverride(label);
			return true;
		}
		const fb = this._visibleAnchor(visual, label, n);
		if(!fb){
			clearLabelAnchorOverride(label);
			return false;
		}
		setLabelAnchorOverride(label, fb.x, fb.y, fb.z);
		return this._project(fb.x, fb.y, fb.z, out);
	}

	// Midten af det længste stykke af objektets kanter, der ligger inden for (indsat) arbejdsflade og inden for synsfeltets dybde. Kanter med en
	// ende bag kameraet klippes mod nærplanet, så et objekt, der omslutter kameraet, stadig kan forankres. Det forrige stykke foretrækkes, så længe
	// det er næsten lige så langt (undgår spring mellem kanter).
	_visibleAnchor(visual, label, n){
		const rules = this.rules;
		const inset = rules.anchorReturnInset;
		const bx0 = inset, by0 = inset, bx1 = this.W - inset, by1 = this.H - inset;
		if(bx1 <= bx0 || by1 <= by0) return null;
		const u = label.userData;
		const prev = u.pvFbSeg === undefined ? -1 : u.pvFbSeg;
		const segmentCount = visual.closed ? n : n - 1;
		const e = this.pv.elements;
		const near = this.near * 1.01, far = this.far * 0.99;
		const tmpA = this._tmp2 || (this._tmp2 = {x: 0, y: 0, f: 1, ppm: 0}), tmpB = this._tmp3 || (this._tmp3 = {x: 0, y: 0, f: 1, ppm: 0});
		let best = -1, bestLen = 0, bestT = 0.5, prevLen = 0, prevT = 0.5;
		for(let i = 0; i < segmentCount; i++){
			const j = (i + 1) % n;
			const p = visual.points[i].position, q = visual.points[j].position;
			// dybdeintervallet [ta, tb] af kanten (verdensparameter), hvor w (dybden) ligger mellem nær- og fjernplan
			let ta = 0, tb = 1;
			if(!this.ortho){
				const wp = e[3] * p.x + e[7] * p.y + e[11] * p.z + e[15], wq = e[3] * q.x + e[7] * q.y + e[11] * q.z + e[15];
				const dw = wq - wp;
				if(Math.abs(dw) < 1e-12){ if(wp < near || wp > far) continue; }
				else{
					let t1 = (near - wp) / dw, t2 = (far - wp) / dw;
					if(t1 > t2){ const x = t1; t1 = t2; t2 = x; }
					ta = Math.max(0, t1); tb = Math.min(1, t2);
					if(tb <= ta) continue;
				}
			}
			const ax = p.x + (q.x - p.x) * ta, ay = p.y + (q.y - p.y) * ta, az = p.z + (q.z - p.z) * ta;
			const bx = p.x + (q.x - p.x) * tb, by = p.y + (q.y - p.y) * tb, bz = p.z + (q.z - p.z) * tb;
			if(!this._project(ax, ay, az, tmpA) || !this._project(bx, by, bz, tmpB)) continue;
			const x0 = tmpA.x, y0 = tmpA.y, dx = tmpB.x - x0, dy = tmpB.y - y0;
			let t0 = 0, t1 = 1, ok = true;
			for(let k = 0; k < 4 && ok; k++){
				let pp, qq;
				if(k === 0){ pp = -dx; qq = x0 - bx0; }
				else if(k === 1){ pp = dx; qq = bx1 - x0; }
				else if(k === 2){ pp = -dy; qq = y0 - by0; }
				else { pp = dy; qq = by1 - y0; }
				if(pp === 0){ if(qq < 0) ok = false; }
				else{
					const r = qq / pp;
					if(pp < 0){ if(r > t1) ok = false; else if(r > t0) t0 = r; }
					else{ if(r < t0) ok = false; else if(r < t1) t1 = r; }
				}
			}
			if(!ok || t1 <= t0) continue;
			const len = (t1 - t0) * Math.hypot(dx, dy);
			// skærmparameter -> verdensparameter (perspektivkorrekt): 1/w interpolerer lineært på skærmen
			const sMid = (t0 + t1) / 2;
			let tWorld = ta + (tb - ta) * sMid;
			if(!this.ortho){
				const wa = e[3] * ax + e[7] * ay + e[11] * az + e[15], wb = e[3] * bx + e[7] * by + e[11] * bz + e[15];
				const wS = 1 / ((1 - sMid) / wa + sMid / wb);
				const dwab = wb - wa;
				if(Math.abs(dwab) > 1e-12) tWorld = ta + (tb - ta) * ((wS - wa) / dwab);
			}
			if(len > bestLen){ bestLen = len; best = i; bestT = tWorld; }
			if(i === prev){ prevLen = len; prevT = tWorld; }
		}
		if(best < 0 || bestLen < rules.anchorMinPx){ u.pvFbSeg = undefined; return null; }
		if(prev >= 0 && prevLen >= rules.anchorMinPx && prevLen >= bestLen * 0.7){ best = prev; bestT = prevT; }
		u.pvFbSeg = best;
		const p = visual.points[best].position, q = visual.points[(best + 1) % n].position;
		const out = this._anchorOut;
		out.x = p.x + (q.x - p.x) * bestT; out.y = p.y + (q.y - p.y) * bestT; out.z = p.z + (q.z - p.z) * bestT;
		return out;
	}

	// ---------------------------------------------------------------------------------------------------------------------
	// Labels uden for manageren som forhindringer. De styres ikke og får ingen regler; kun deres synlige skærmrektangel læses.

	_scanLabelTree(object, depth){
		const children = object.children;
		for(let i = 0; i < children.length; i++){
			if(--this._scanBudget < 0) return; // en meget stor scene må ikke gøre scanningen dyr
			const child = children[i];
			if(child instanceof TextSprite){
				if(!child.userData.pvRec) this.extLabels.push(child);
				continue;
			}
			// de styrede objekter har deres egne labels; punktskyer gennemsøges ikke (andre objekter højst fire niveauer ned - markørkugler bærer fx
			// native Measure-labels)
			if(child.isFeatureVisual || child.isPipeVisual || child.isPointCloudTree) continue;
			if(depth < 4 && child.children.length > 0) this._scanLabelTree(child, depth + 1);
		}
	}

	_scanExternalLabels(){
		this.extLabels.length = 0;
		this._scanBudget = 6000;
		const viewer = this.module.viewer;
		if(viewer.scene && viewer.scene.scene) this._scanLabelTree(viewer.scene.scene, 0);
		// native målinger og volumener lever i værktøjernes egne scener (viewer.measuringTool.scene, viewer.volumeTool.scene ...)
		for(const key of ["measuringTool", "volumeTool", "profileTool"]){
			const tool = viewer[key];
			if(tool && tool.scene && tool.scene.children) this._scanLabelTree(tool.scene, 0);
		}
		this._scanLabelTree(this.module.scene, 0);
	}

	_externallyVisible(label){
		for(let o = label; o; o = o.parent) if(!o.visible) return false;
		return true;
	}

	// Samler positioner, skala og tekstlængder af de synlige eksterne labels: ændres summen, flyttes forhindringerne
	_externalSignature(){
		let sum = 0, count = 0;
		const labels = this.extLabels;
		for(let i = 0; i < labels.length; i++){
			const label = labels[i];
			if(!label.visible || !this._externallyVisible(label)) continue;
			const e = label.matrixWorld.elements;
			sum += e[12] * 1.01 + e[13] * 1.03 + e[14] * 1.07 + e[0] * 3.1 + label.text.length * 0.37 + (i + 1) * 0.011;
			count++;
		}
		return sum + count * 1e6;
	}

	_collectExternal(){
		const labels = this.extLabels;
		const tmp = this._tmp;
		for(let i = 0; i < labels.length; i++){
			const label = labels[i];
			if(!label.visible || !label.text || !this._externallyVisible(label)) continue;
			const e = label.matrixWorld.elements;
			if(!this._project(e[12], e[13], e[14], tmp)) continue;
			// skærmstørrelse = teksturens størrelse (verdensenheder: sprite.scale) x labelens verdensskala x px pr. meter ved labelen
			const worldScale = Math.hypot(e[0], e[1], e[2]);
			const w = label.sprite.scale.x * worldScale * tmp.ppm, h = label.sprite.scale.y * worldScale * tmp.ppm;
			if(!(w > 0) || !(h > 0)) continue;
			const x0 = tmp.x - w / 2, y0 = tmp.y - h / 2, x1 = tmp.x + w / 2, y1 = tmp.y + h / 2;
			if(x1 <= 0 || y1 <= 0 || x0 >= this.W || y0 >= this.H) continue;
			this.obstacles.push({x0, y0, x1, y1});
		}
	}

	// Et objekt uden for synsfeltet får ikke opdateret sine labels' skala (DigitizationModule.update springer det over). Dets labels skjules, så en
	// label med forældet skala aldrig tegnes; når objektet kommer tilbage, sætter næste layout dem frem igen. Ikke "skjult automatisk" for brugeren.
	hideCulled(visual){
		const hide = (label) => {
			if(!label || !label.visible) return;
			label.userData.pvAuto = true;
			applyLabelVisibility(label);
		};
		hide(visual.resultLabel);
		for(const l of visual.segmentLabels) hide(l);
		for(const l of visual.vertexLabels) hide(l);
		const aux = visual.userData.auxiliaryVisual;
		if(aux && typeof aux.auxLabels === "function") for(const l of aux.auxLabels()) hide(l);
	}

	_keepOffscreen(visual){
		const keep = (label) => {
			const rec = label.userData.pvRec;
			if(!rec) return;
			rec.onScreen = false;
			rec.maxLevel = LEVEL.HIDDEN;
			this.recs.push(rec);
		};
		if(visual.resultLabel) keep(visual.resultLabel);
		for(const l of visual.segmentLabels) keep(l);
		for(const l of visual.vertexLabels) keep(l);
		const aux = visual.userData.auxiliaryVisual;
		if(aux && typeof aux.auxLabels === "function") for(const l of aux.auxLabels()) keep(l);
	}

	// Skriver resultatet tilbage på labels. Returnerer true, hvis noget synligt er ændret (så der skal tegnes igen).
	_apply(){
		let changed = false;
		const stamp = this.stamp;
		const active = this.previous;
		active.length = 0;
		let shown = 0, forced = 0;
		const autoLabels = this._autoLabels, autoResults = this._autoResults;
		autoLabels.clear(); autoResults.clear();

		for(const rec of this.recs){
			const label = rec.label;
			const u = label.userData;
			rec.stamp = stamp;
			active.push(rec);

			if(rec.onScreen === false){ rec.onScreen = true; continue; }

			const visibleBefore = label.visible;
			const levelBefore = u.pvLevel;
			const dxBefore = u.pvDx || 0, dyBefore = u.pvDy || 0;

			if(rec.maxLevel === LEVEL.HIDDEN){
				rec.visible = false; rec.level = LEVEL.HIDDEN; rec.slot = null; rec.leader = false;
				u.pvAuto = !!u.pvWanted; // brugeren vil se den, men zoom/kvote/plads skjuler den
				u.pvPeek = false;
			}else if(rec.visible){
				u.pvAuto = false;
				u.pvPeek = rec.peek;
				setLabelLevel(label, rec.level);
				u.pvDx = rec.cx - rec.ax;
				u.pvDy = rec.cy - rec.ay;
				u.pvLeader = rec.leader;
				label.sprite.renderOrder = RENDER_ORDER[rec.tier];
				shown++;
				if(rec.reason === "forced") forced++;
			}else{
				u.pvAuto = !!u.pvWanted;
				u.pvPeek = false;
				u.pvDx = 0; u.pvDy = 0; u.pvLeader = false;
			}
			applyLabelVisibility(label);
			if(u.pvWanted && u.pvAuto && !rec.silent){
				if(rec.kind === "result" || rec.kind === "pipe") autoResults.add(rec.owner_); else autoLabels.add(rec.owner_);
			}

			if(label.visible !== visibleBefore || u.pvLevel !== levelBefore || (u.pvDx || 0) !== dxBefore || (u.pvDy || 0) !== dyBefore) changed = true;
		}

		// labels, der ikke længere indgår (fravalgt, andet objekt valgt ...): tilbage til ren brugerstyring
		const previousActive = this.active;
		for(const rec of previousActive){
			if(rec.stamp === stamp) continue;
			resetLabelManagement(rec.label);
			rec.visible = false; rec.slot = null; rec.level = LEVEL.HIDDEN; rec.leader = false;
			changed = true;
		}
		// de aktuelle bliver "forrige" ved næste layout
		this.active = active;
		this.previous = previousActive;

		// forbindelseslinjer: de synlige labels, der ligger væk fra deres anker
		this.leaders.length = 0;
		for(const rec of this.recs){
			if(rec.visible && rec.leader && rec.label.visible) this.leaders.push(rec);
		}

		// "skjult automatisk" gøres synligt i Registreringer (label-ikonet dæmpes): kun når tilstanden skifter for et objekt
		this._publishAutoState(autoLabels, autoResults);

		this.stats.shown = shown;
		this.stats.forced = forced;
		return changed;
	}

	_publishAutoState(autoLabels, autoResults){
		const visuals = this._autoVisuals || (this._autoVisuals = new Set());
		for(const v of autoLabels) visuals.add(v);
		for(const v of autoResults) visuals.add(v);
		for(const v of this._autoPrevious || []) visuals.add(v);
		// alle nuværende registreringer afstemmes ved hvert layout, så en markering aldrig overlever, at objektet er rykket ud af billedet,
		// er skiftet, skjult, ændret eller fjernet
		for(const v of this.module.visuals.values()) visuals.add(v);
		let any = false;
		for(const v of visuals){
			const labels = autoLabels.has(v), results = autoResults.has(v);
			const ud = v.userData;
			if(!!ud.pvAutoLabels !== labels || !!ud.pvAutoResult !== results){
				ud.pvAutoLabels = labels; ud.pvAutoResult = results;
				any = true;
			}
		}
		visuals.clear();
		const prev = this._autoPrevious || (this._autoPrevious = []);
		prev.length = 0;
		for(const v of autoLabels) prev.push(v);
		for(const v of autoResults) if(!autoLabels.has(v)) prev.push(v);
		if(any){
			// samme hændelse som Registreringer allerede lytter på ved ændret label-tilstand
			this.module.dispatchEvent({type: "label_visibility_changed", source: "auto"});
		}
	}

	// ---------------------------------------------------------------------------------------------------------------------
	// Forbindelseslinjer: ét LineSegments-objekt for alle (ét draw call), i koordinater relativt til en origo (UTM-koordinater er for store
	// til float32)

	_ensureLeaderObject(){
		if(this.leaderObject) return;
		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(MAX_LEADERS * 2 * 3), 3));
		geometry.setDrawRange(0, 0);
		const material = new THREE.LineBasicMaterial({color: 0xcfe8ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false});
		const lines = new THREE.LineSegments(geometry, material);
		lines.frustumCulled = false;
		lines.renderOrder = 9;
		lines.visible = false;
		lines.name = "label-leaders";
		this.module.scene.add(lines);
		this.leaderObject = lines;
	}

	_updateLeaders(){
		const count = Math.min(this.leaders.length, MAX_LEADERS);
		if(count === 0){
			if(this.leaderObject && this.leaderObject.visible){
				this.leaderObject.visible = false;
				this._requestRedraw();
			}
			return;
		}
		this._ensureLeaderObject();
		const lines = this.leaderObject;
		const attr = lines.geometry.getAttribute("position");
		const a = attr.array;
		const origin = this.leaders[0].label.position;
		lines.position.set(origin.x, origin.y, origin.z);
		lines.updateMatrixWorld(true);

		const right = this.right, up = this.up;
		let n = 0;
		for(let i = 0; i < count; i++){
			const rec = this.leaders[i];
			const label = rec.label;
			const k = label.scale.x / (PX_PER_LOCAL * rec.f); // verdensenheder pr. px ved labelen (på tværs af blikretningen)
			const size = rec.sizes[rec.level];
			if(!size) continue;
			const hw = size.w / 2, hh = size.h / 2;
			// punktet på labelens kant nærmest ankeret (relativt til ankeret, px, y nedad)
			const lx = rec.cx - rec.ax, ly = rec.cy - rec.ay;
			const ex = Math.max(lx - hw, Math.min(0, lx + hw));
			const ey = Math.max(ly - hh, Math.min(0, ly + hh));
			const p = label.position;
			const bx = p.x - origin.x, by = p.y - origin.y, bz = p.z - origin.z;
			a[n++] = bx; a[n++] = by; a[n++] = bz;
			a[n++] = bx + (right.x * ex - up.x * ey) * k;
			a[n++] = by + (right.y * ex - up.y * ey) * k;
			a[n++] = bz + (right.z * ex - up.z * ey) * k;
		}
		attr.needsUpdate = true;
		lines.geometry.setDrawRange(0, n / 3);
		lines.visible = n > 0;
	}

	dispose(){
		setHoverChangeListener(null);
		if(this.leaderObject){
			this.module.scene.remove(this.leaderObject);
			this.leaderObject.geometry.dispose();
			this.leaderObject.material.dispose();
			this.leaderObject = null;
		}
	}
}
